import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

const ChangePassword = () => {
  const [form, setForm] = useState({
    current: "",
    new: "",
    confirm: "",
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [avatar, setAvatar] = useState("https://randomuser.me/api/portraits/men/32.jpg");
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
    setSuccess("");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!form.current || !form.new || !form.confirm) {
      setError("All fields are required.");
      return;
    }
    if (form.new.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (form.new !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSuccess("Password changed successfully!");
      setForm({ current: "", new: "", confirm: "" });
    }, 1200);
  };

  const handleAvatarClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.match(/^image\/(jpeg|png)$/)) {
      setError("Only JPG and PNG images are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image size must be less than 5MB.");
      return;
    }
    const url = URL.createObjectURL(file);
    setAvatar(url);
    setError("");
  };

  return (
    <div className="min-h-screen flex flex-col py-4 px-2 sm:px-4 md:px-8 items-center justify-center">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-2xl bg-white rounded-2xl shadow-xl border-0 p-6 sm:p-8 flex flex-col gap-6 relative"
      >
        <div className="flex flex-col items-center gap-2 mb-2">
          <img
            src={avatar}
            alt="Profile"
            className="w-20 h-20 rounded-full object-cover border-2 border-orange-200 shadow-sm"
          />
          <input
            type="file"
            accept="image/jpeg,image/png"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleAvatarChange}
          />
          <button
            type="button"
            className="px-3 py-1 rounded-xl bg-gradient-to-r from-orange to-orange2 text-white font-semibold text-sm hover:from-orange2 hover:to-orange transition mt-2 shadow-md border-0"
            onClick={handleAvatarClick}
          >
            Change Avatar
          </button>
          <span className="text-xs text-gray-500 mt-1">
            Supported formats: JPG, PNG (Max 5MB)
          </span>
        </div>
        <hr className="my-2" />
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <label
              htmlFor="current"
              className="text-sm font-medium text-gray-700 sm:w-44"
            >
              Current Password
            </label>
            <input
              id="current"
              name="current"
              type="password"
              autoComplete="current-password"
              className="flex-1 px-4 py-2 rounded-xl border border-orange bg-white text-gray-700 text-base focus:outline-none focus:ring-2 focus:ring-orange2 shadow-sm"
              value={form.current}
              onChange={handleChange}
              required
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <label htmlFor="new" className="text-sm font-medium text-gray-700 sm:w-44">
              New Password
            </label>
            <input
              id="new"
              name="new"
              type="password"
              autoComplete="new-password"
              className="flex-1 px-4 py-2 rounded-xl border border-orange bg-white text-gray-700 text-base focus:outline-none focus:ring-2 focus:ring-orange2 shadow-sm"
              value={form.new}
              onChange={handleChange}
              required
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <label
              htmlFor="confirm"
              className="text-sm font-medium text-gray-700 sm:w-44"
            >
              Confirm New Password
            </label>
            <input
              id="confirm"
              name="confirm"
              type="password"
              autoComplete="new-password"
              className="flex-1 px-4 py-2 rounded-xl border border-orange bg-white text-gray-700 text-base focus:outline-none focus:ring-2 focus:ring-orange2 shadow-sm"
              value={form.confirm}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        <div className="bg-orange-50 text-orange-700 px-4 py-2 rounded-xl text-sm font-medium border border-orange-200 flex items-center gap-2 mt-2">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"
            />
          </svg>
          Changing your password will require re-login
        </div>
        {error && (
          <div className="bg-red-100 text-red-700 px-4 py-2 rounded-xl text-sm font-medium border border-red-200 flex items-center gap-2 mt-2">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"
              />
            </svg>
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-100 text-green-700 px-4 py-2 rounded-xl text-sm font-medium border border-green-200 flex items-center gap-2 mt-2">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
            {success}
          </div>
        )}
        <div className="flex gap-3 mt-2 justify-end">
          <button
            type="button"
            className="px-5 py-2 rounded-xl border border-orange text-orange-700 bg-white font-semibold hover:bg-orange-50 transition shadow-sm"
            onClick={() => {
              setForm({ current: "", new: "", confirm: "" });
              setAvatar("https://randomuser.me/api/portraits/men/32.jpg");
              navigate("/");
            }}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-5 py-2 rounded-xl bg-gradient-to-r from-orange to-orange2 text-white font-semibold hover:from-orange2 hover:to-orange transition focus:ring-2 focus:ring-orange2 focus:outline-none disabled:opacity-60 shadow-md border-0"
            disabled={loading}
          >
            {loading ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChangePassword;